/**
 * OWA-526 — clean-entity cache staleness.
 *
 * Pure decision core shared by every entity-editing card (via useCachedEntity).
 * Cards cache the user's mid-edit draft so navigating away and back doesn't lose
 * work — but a CLEAN (unedited) entity must NOT be restored from cache, because
 * the cached snapshot diverges from Redux once Socket.IO/other tabs update the
 * server copy (May 2026 Lawson invoice: three different totals from one DB row;
 * OWA-525: a stale running task → inflated hours).
 *
 * Two invariants every card must uphold, both encoded here:
 *   A (no-stale):       a clean remount returns the FRESH (live/Redux) entity.
 *   B (preserve-edits): a dirty remount returns the cached DRAFT, never the live
 *                       entity — dropping unsaved work is a worse bug than stale.
 */

/** The minimum an entity must expose for staleness comparison (Base provides both). */
export interface DbStamped {
    /** Server-assigned write timestamp; the canonical "which is newer" signal. */
    dbAddedOn?: number | null;
    /** Client high-res timestamp; fallback when dbAddedOn is not yet populated. */
    addedOn?: number | null;
}

/** What a card stores in the component cache for its edited entity. */
export interface CachedEntityRecord<E> {
    current?: E | null;
    hasChanged?: boolean;
}

/**
 * Is the live (Redux) entity at least as new as the cached one? Mirrors the
 * OWA-7 `isLiveTaskNewer` idiom: prefer the server stamp `dbAddedOn`, fall back
 * to the client `addedOn` when either side lacks a server stamp. ">=" (not ">")
 * so that on a tie we treat live as fresh and drop the redundant cached copy.
 *
 * @param {DbStamped} live - the entity from Redux/props
 * @param {DbStamped} cached - the entity restored from the component cache
 * @returns {boolean} true when live is newer-or-equal to cached
 */
export function isLiveNewerOrEqual(live: DbStamped | null | undefined, cached: DbStamped | null | undefined): boolean {
    if (!live) return false;
    if (!cached) return true;

    const liveDb = live.dbAddedOn ?? null;
    const cachedDb = cached.dbAddedOn ?? null;
    if (liveDb != null && cachedDb != null) return liveDb >= cachedDb;

    const liveAdded = live.addedOn ?? 0;
    const cachedAdded = cached.addedOn ?? 0;
    return liveAdded >= cachedAdded;
}

/**
 * Decide which entity a card should mount with, given its cached record and the
 * current live (Redux) entity.
 *
 * - nothing cached / no cached entity → live (fresh).
 * - cached.hasChanged → cached draft (INVARIANT B: preserve the user's edits).
 * - clean cache → live when live is newer-or-equal (INVARIANT A: self-heal a
 *   stale snapshot), otherwise the cached copy (rare: cache is somehow newer,
 *   e.g. live not yet round-tripped — keep the richer copy rather than regress).
 * - no live counterpart (brand-new/offline entity) → cached draft.
 *
 * @param {CachedEntityRecord<E>} cached - the component-cache record (may be missing)
 * @param {E} live - the live entity from Redux/props
 * @returns {E} the entity to initialize component state with
 */
export function chooseEntity<E extends DbStamped>(
    cached: CachedEntityRecord<E> | null | undefined,
    live: E,
): E {
    if (!cached || cached.current == null) return live;
    if (cached.hasChanged) return cached.current;          // Invariant B
    if (live == null) return cached.current;               // no live counterpart
    return isLiveNewerOrEqual(live, cached.current) ? live : cached.current; // Invariant A
}
